
/*
 * Copyright (c) 2023. Data Smart Technologies CC
 * https://www.datasmart.co.za
 * View license agreement and terms of use at https://www.datasmart.co.za/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2023-06-29
 */

import React, { ChangeEvent, useEffect, useState } from "react";
import { Button, Card, Container, Modal } from "react-bootstrap";
import { useAuth } from "../../../../contexts/AuthContext";
import { QueryListProvider } from "../../../../component/solution/QueryList/QueryListContext";
import { QueryList } from "../../../../component/solution/QueryList/QueryList";
import { Search, X } from "lucide-react";
import { formatDateFull } from "../../../../component/solution/utils/AppUtil";
import { CastType, IQuery, IWhere } from "../../../../component/solution/Interfaces";

export const ReturnRequest: React.FC = () => {

    const {fetchData, token}  = useAuth();
    const [resultElements, setResultElements] = useState<React.ReactElement[]>([]);
    const [detailElements, setDetailElements] = useState<React.ReactElement[]>([]);
    const [search, setSearch] = useState<Map<string, string>>(new Map<string, string>());
    // Modal Show & Close
    const [showModal, setShowModal] = useState(false);
    const [docNo, setDocNo] = useState<number>(0);

    const queryUrl: string = `${process.env.REACT_APP_BASE_URL}/${"query/data"}`;
    const [queryState, setQueryState] = useState<IQuery>(
        {
            tableName: "sa_m24l_return_request",
            select: [],
            groupBy: [],
            join: [],
            orderBy: [ 
                {
                    fullColumnName: "doc_num",
                    direction: "DESC",
                    cast: CastType.INTEGER
                }
            ],
            where: [],
            mainTableOnly: false,
            includeExtraColumns: false,
            includeControlColumns: true,
            modifiedAfter: null,
            touchedBefore: null,
            isDirty: null,
            pageNum: 0,
            pageSize: 50,
            frontEndOnly: null
        }        
    );

   
    const detailQueryState: IQuery = {
            tableName: "sa_m24l_return_request_line",
            select: [
            ],
            groupBy: [],
            join: [],
            orderBy: [
                {
                    fullColumnName: "item_code",
                    direction: "ASC",
                    cast: CastType.STRING
                }
            ],
            where: [
                {
                    fullColumnName: "doc_num",
                    value: "",
                    operator: "EQ",
                    cast: CastType.STRING
                }
            ],
            mainTableOnly: false,
            includeExtraColumns: false,
            includeControlColumns: true,
            modifiedAfter: null,
            touchedBefore: null,
            isDirty: null,
            pageNum: 0,
            pageSize: 5000,
            frontEndOnly: null
        }


    const generateDetailTables = (detail: any[]) => {

        const detailTable: React.ReactElement[] = [];
        let totalQuantityOrdered = 0;
        let totalQuantityReceived = 0;

        // Sort by line_num ascending before rendering
        const sortedDetail = [...detail].sort((a, b) => {
            const aLine = a.data?.line_num ?? 0;
            const bLine = b.data?.line_num ?? 0;
            return aLine - bLine;
        });

        sortedDetail.forEach((item, idx) => {
            const data = item.data;
            totalQuantityOrdered += data?.qty_ordered ?? 0;
            totalQuantityReceived += data?.qty_received ?? 0;

            detailTable.push(
            <tr key={idx}>
                <td className="text-center" style={{ border: '1px solid #adb5bd' }}>{data?.line_num}</td>
                <td className="text-center" style={{ border: '1px solid #adb5bd' }}>{data?.item_code}</td>
                <td className="text-center" style={{ border: '1px solid #adb5bd' }}>{data?.handling_unit}</td>
                <td className="text-center" style={{ border: '1px solid #adb5bd' }}>{data?.warehouse_code}</td>
                <td className="text-center" style={{ border: '1px solid #adb5bd' }}>{data?.qty_ordered}</td>
                <td className="text-center" style={{ border: '1px solid #adb5bd' }}>{data?.qty_received}</td>
            </tr>
            );
        });

        const summaryTable = (
            <table className="table w-auto" style={{ border: '1px solid #adb5bd' }}>
            <tbody>
                <tr>
                    <th style={{ width: '150px', border: '1px solid #adb5bd' }}>Unique Item Total</th>
                    <td style={{ width: '100px',  border: '1px solid #adb5bd' }} className="text-end">{detail.length}</td>
                </tr>
                    <tr>
                    <th style={{ width: '150px', border: '1px solid #adb5bd' }}>Total Qty Ordered</th>
                    <td style={{ width: '100px',  border: '1px solid #adb5bd' }} className="text-end">{totalQuantityOrdered}</td>
                </tr>
                <tr>
                    <th style={{ width: '150px', border: '1px solid #adb5bd' }}>Total Qty Recieved</th>
                    <td style={{ width: '100px',  border: '1px solid #adb5bd' }} className="text-end">{totalQuantityReceived}</td>
                </tr>
            </tbody>
        </table>
        );

        return { summaryTable, detailTable };
    };



    /* 
     Status 

     Inbound - Return Request
        TO_SEND - Document ready to be sent to M24L.
        SENT - Doument has has been sent to M24L for processing.
        SUCCESS - Document successfully received at M24L for processing.
        ERROR - There was a problem integrating the document at M24L.
        ARRIVED - Shipment has arrived at M24L.
        CLOSED - Document processing complete.

    */

    return(
        <React.Fragment>
            <Container fluid className="p-0">

            <QueryListProvider>
                    <QueryList
                        listTitle={"Inbound - Return Requests"}
                        recordTitle={"Inbound - Return Requests"}
                        tableName={"sa_m24l_return_request"}
                        editTableName={"sa_m24l_return_request"}
                        hasIntegrationStatusLegend={true} 
                        errorCategoryName={'ReturnRequest'}
                        integrationstatusLegend={[
                            { status: 'ERROR', color: '#bf3636', description: 'There was a problem integrating the document at M24L'},
                            { status: 'TO_SEND', color: '#d4a53f', description: 'Document ready to be sent to M24L.'},
                            { status: 'ARRIVED', color: '#d4a53f', description: 'Shipment has arrived at M24L.'},
                            { status: 'SENT', color: '#d4a53f', description: 'Doument has has been sent to M24L for processing.'},
                            { status: 'SUCCESS', color: '#d4a53f', description: 'Document successfully received at M24L for processing.'},
                            { status: 'CLOSED', color: '#3ec76b', description: 'Document processing complete.'}
                        ]}
                        modal={
                            {
                              modalTitle: "Doc Num:",
                              modalSubtitle: "Inbound - Return Request:",  //Shipment Confirmation
                              modalProptBy: "doc_num",
                              tableName: "sa_m24l_return_request",
                              tableLineQuery: "sa_m24l_return_request_line",
                              modalAction: 'viewing',
                              modalHasViewing: true,
                              queryData: detailQueryState,
                              processData: (result: any) : JSX.Element => {
                                 
                                const detail: any[] = result.data;
                                const { summaryTable, detailTable } = generateDetailTables(detail);

                                return (
                                     <div className="d-flex flex-column gap-3" >
                                        <div style={{background:"#f2f5f3"}} className="p-2">
                                            <h6 className="text-secondary fw-bolder">
                                               Summary
                                            </h6>
                                            <div className="mb-3">{summaryTable}</div>
                                        </div>

                                        <div style={{background:"#f2f5f3"}} className="p-2">
                                            <h6 className="text-secondary fw-bolder">
                                                Line Details
                                            </h6>
                                            <table className="table" style={{ border: '1px solid #adb5bd' }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ border: '1px solid #adb5bd' }}>Line No.</th>
                                                        <th style={{ border: '1px solid #adb5bd' }}>Item Code</th>
                                                        <th style={{ border: '1px solid #adb5bd' }}>Handling Unit</th>
                                                        <th style={{ border: '1px solid #adb5bd' }}>Warehouse Code</th>
                                                        <th style={{ border: '1px solid #adb5bd' }}>Qty Ordered</th>
                                                        <th style={{ border: '1px solid #adb5bd' }}>Qty Received</th>
                                                    </tr>
                                                </thead>
                                                <tbody>{detailTable}</tbody>
                                            </table>
                                        </div>
                                    </div>
                                 )
                               }
                            }
                        }
                        editHasCompoundKey={false}
                        join={[{
                            relationship: "notifications",
                            joinType: "LEFT"
                        }]} 
                        select={[
                            {
                                fullColumnName: "doc_num",
                                aggregate: ""
                            },
                            {
                                fullColumnName: "doc_type",
                                aggregate: ""
                            },
                            {
                                fullColumnName: "division",
                                aggregate: ""
                            },
                            {
                                fullColumnName: "rma_num",
                                aggregate: ""
                            },   
                            {
                                fullColumnName: "card_code",
                                aggregate: ""
                            },              
                            {
                                fullColumnName: "card_name",
                                aggregate: ""
                            }, 
                            {
                                fullColumnName: "num_at_card",
                                aggregate: ""
                            }, 
                            {
                                fullColumnName: "integration_status",
                                aggregate: ""
                            },     
                            {
                                fullColumnName: "total_qty",
                                aggregate: ""
                            },    
                            {
                                fullColumnName: "comments",
                                aggregate: ""
                            },                                      
                            {
                                fullColumnName: "created",
                                aggregate: ""
                            },
                            {
                                fullColumnName: "updated",
                                aggregate: ""
                            },
                            {
                                fullColumnName: "notifications.reference",
                                aggregate: "COUNT"
                            },
                        ]}
                        where={[
                        //     {
                        //     "fullColumnName": "notifications.handled",
                        //     "value": false,
                        //     "operator": "EQ",
                        //     "cast": CastType.BOOLEAN
                        //  }, 
                        ]} 
                        orderBy={[    
                            {
                                fullColumnName: "doc_num",
                                direction: "DESC",
                                cast: CastType.INTEGER
                            }
                         ]} 
                        groupBy={[
                            "doc_num",
                            "doc_type",
                            "division",
                            "rma_num",
                            "card_code",
                            "card_name",
                            "num_at_card",
                            "integration_status",
                            "total_qty",
                            "comments",
                            "created",
                            "updated"
                        ]}                   
                    />
                </QueryListProvider>

                {/* <Card>
                    <Card.Header>
                        <Card.Title>{"Inbound - Return Requests"}</Card.Title>
                    </Card.Header>

                    <div className="ms-4 me-4" style={{}}>

                        <table className="table table-bordered">
                            <thead>
                                <tr>
                                    {Array.from(search).map(([key, value]) => (
                                        <td>
                                            <input id={key} name={key} value={value} onChange={handleOnChange} type="text" autoComplete="off" placeholder={`Search ${key}...`} className="form-control form-control-md"></input>
                                        </td>
                                    ))}
                                    <td colSpan={2}><Search color="#c84338" onClick={searchClicked} className="custom-icon text-center align-middle" /><X color="#c84338" onClick={clearClicked} style={{width: "36px", height: "36px"}} className="custom-icon text-center align-middle ms-5"/></td>
                                    <td></td>
                                </tr>                              
                                <tr>
                                    <td><b>DocNo</b></td>
                                    <td><b>DocType</b></td>
                                    <td><b>Division</b></td>
                                    <td><b>RMA</b></td>
                                    <td><b>CardCode</b></td>
                                    <td><b>NumAtCard</b></td>
                                    <td><b>CardName</b></td>
                                    <td><b>Status</b></td>
                                    <td><b>Created</b></td>
                                    <td><b>Updated</b></td>
                                </tr>
                            </thead>
                            <tbody>
                                {resultElements}                                                                                                                                                              
                            </tbody>
                        </table>
                    </div>

                    <>
                        
                        <Modal show={showModal} onHide={() => setShowModal(false)}>
                            <Modal.Header closeButton>
                            <Modal.Title>Doc Num: {docNo}</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <p><b>Shipment Confirmation:</b></p>            
                                <table className="table table-bordered">
                                    <thead>
                                        <tr>
                                            <td><b>Line No.</b></td>
                                            <td><b>Item Code</b></td>
                                            <td><b>Handling Unit</b></td>
                                            <td><b>Warehouse Code</b></td>
                                            <td><b>Quantity Ordered</b></td>
                                            <td><b>Quantity Received</b></td>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detailElements}
                                    </tbody>
                                </table>
                            </Modal.Body>
                            <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowModal(false)}>
                                Close
                            </Button>
                            </Modal.Footer>
                        </Modal>
                    </>                    

                </Card> */}
            </Container>
        </React.Fragment>
    );
}